import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import Box from '@mui/material/Box';
import { DateRange, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';

/**
 * Shape of the finalized query parameters object generated from the selected date range.
 * Used directly by backend pagination services filtering on timestamp intervals.
 *
 * @interface DateRangeQueryParams
 * @property {string} timestamp__gte - ISO/Formatted start date representing "Greater Than or Equal to" [YYYY-MM-DD].
 * @property {string} timestamp__lte - ISO/Formatted end date representing "Less Than or Equal to" [YYYY-MM-DD].
 */
export interface DateRangeQueryParams {
  timestamp__gte: string;
  timestamp__lte: string;
}

/**
 * Props definition for the EnhancedDateRangePicker component.
 *
 * @interface EnhancedDateRangePickerProps
 */
interface EnhancedDateRangePickerProps {
  /**
   * Il valore corrente dell'intervallo di date rappresentato come tupla [Dayjs, Dayjs].
   */
  value: DateRange<Dayjs>;

  /**
   * Callback attivata ad ogni singolo click di selezione sul calendario (sia per la data di inizio che per la fine).
   */
  onChange: (value: DateRange<Dayjs>) => void;

  /**
   * Callback di conferma del filtro.
   * - Se `closeOnSelect={false}`, viene scatenata **esplicitamente** quando l'utente clicca sul pulsante di conferma nell'Action Bar (es. per lanciare chiamate API evitando refresh parziali).
   * - Se `closeOnSelect={true}`, viene scatenata **automaticamente** alla chiusura del calendario non appena la tupla è completa.
   */
  onAccept?: (value: DateRange<Dayjs>) => void;

  /**
   * Il formato di visualizzazione della data all'interno dei campi di testo.
   * @default 'DD/MM/YYYY'
   */
  format?: string;

  /**
   * Determina il comportamento di chiusura del popup e l'applicazione del filtro:
   * - `false`: Il calendario resta aperto al completamento delle date e richiede una conferma esplicita tramite pulsante "Conferma" nell'Action Bar per attivare `onAccept`.
   * - `true`: Il calendario si chiude istantaneamente da solo non appena viene cliccata la data di fine.
   * @default false
   */
  closeOnSelect?: boolean;
}

/**
 * A highly compact, generic, and fully controlled Date Range Picker component.
 * Wraps MUI X Pro `DateRangePicker` with optimized layouts tailored for toolbar headers and navigation panels.
 *
 * **Key Architectural Highlights:**
 * - **Controlled Design:** Offloads state persistence entirely to the parent container to mitigate async rendering inconsistencies.
 * - **Optimized Footprint:** Employs narrow text fields (`110px`), localized inline typography (`0.875rem`), and standard Material variants to maximize horizontal toolbar area.
 * - **Explicit Action Gate:** Inhibits automatic calendar closing on selection steps (`closeOnSelect={false}`). Explicit confirmation relies on an integrated, localized Action Bar ("OK" / "Cancel").
 * - **Stateful Divider:** Customizes the layout connector slot with a dynamic icon that alters color context to 'primary' only when an actionable, valid range tuple is supplied.
 *
 * @component
 * @param {EnhancedDateRangePickerProps} props - Configuration options for the controlled picker.
 * @returns {JSX.Element} A themed, fully localized date range selection node.
 */
export function EnhancedDateRangePicker({
  value,
  onChange,
  onAccept,
  format = 'DD/MM/YYYY',
  closeOnSelect = false
}: EnhancedDateRangePickerProps) {
  const { t } = useTranslation();

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DateRangePicker
        format={format}
        value={value}
        onChange={onChange}
        onAccept={onAccept}
        closeOnSelect={closeOnSelect}
        localeText={{
          cancelButtonLabel: t('actions.cancel'),
          okButtonLabel: t('actions.confirm')
        }}
        calendars={2}
        slotProps={{
          textField: ({ position }) => ({
            variant: 'standard',
            size: 'small', // Riduce l'altezza e il padding complessivo
            label: position === 'start' ? t('global.start') : t('global.end'),
            sx: {
              width: '110px', // Limita la larghezza per evitare spazi vuoti a destra della data
              '& .MuiInput-input': {
                fontSize: '0.875rem', // Riduce leggermente il font-size del testo inserito
                pb: 0.5 // Compatta lo spazio sopra la linea di underline dello stile standard
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.85rem' // Riduce il font della label (Inizio / Fine)
              }
            }
          }),
          actionBar: {
            actions: ['cancel', 'accept'] // Mostra i pulsanti "Annulla" e "OK" (o la traduzione locale)
          }
        }}
        slots={{
          fieldSeparator: () => (
            <Box sx={{ mx: 2, display: 'flex', alignItems: 'center' }}>
              <ArrowForwardIosIcon
                color={value[0] && value[1] ? 'primary' : 'disabled'}
                sx={{ fontSize: '1rem', color: '#2479B1' }}
              />
            </Box>
          )
        }}
      />
    </LocalizationProvider>
  );
}
